"use client";

import type { CreateProfileFormData } from "@/features/profile/tutor/schemas/createProfileSchema";

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { type Control, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, toDate } from "date-fns";
import * as z from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { ErrorMessage } from "@hookform/error-message";

import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarWithYearSelect } from "@/components/ui/CalendarWithYear";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface EducationProps {
  control: Control<CreateProfileFormData>;
  setNavButtonsDisabled: Dispatch<SetStateAction<boolean>>;
}

export function Education({ control, setNavButtonsDisabled }: EducationProps) {
  const { t, i18n } = useTranslation(["forms", "common", "profile"]);
  const [currentStep, setCurrentStep] = useState(0); // 0 = list, 1 = add new
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const educationSchema = useMemo(
    () =>
      z
        .object({
          institution_name: z.string().min(3, t("forms:errors.institutionNameMin")).max(255),
          degree: z.string().max(255).optional(),
          field_of_study: z.string().max(255).optional(),
          start_date: z.string().optional(),
          end_date: z.string().optional(),
          description: z.string().max(1000).optional(),
        })
        .refine(
          (data) => {
            if (data.start_date && data.end_date) {
              return data.end_date >= data.start_date;
            }

            return true;
          },
          {
            message: t("forms:errors.endDateAfterStartDate"),
            path: ["end_date"],
          },
        ),
    [t],
  );

  type EducationFormData = z.infer<typeof educationSchema>;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "education",
  });

  const form = useForm<EducationFormData>({
    resolver: zodResolver(educationSchema),
    defaultValues: {
      institution_name: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (currentStep === 1 && fields.length > 0) {
      setNavButtonsDisabled(true);
    } else {
      setNavButtonsDisabled(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 0) {
      form.reset({
        institution_name: "",
      });
    }
  }, [currentStep]);

  const handleAddEducation = useCallback(
    (data: EducationFormData) => {
      const result = educationSchema.safeParse(data);

      if (result.success) {
        const formattedData = {
          ...result.data,
          start_date: result.data.start_date ?? undefined,
          end_date: result.data.end_date ?? undefined,
        };

        if (editingIndex !== null) {
          // Update existing education
          update(editingIndex, formattedData);
          setEditingIndex(null);
        } else {
          // Add new education
          append(formattedData);
        }

        form.reset();
        setCurrentStep(0);

        return true;
      } else {
        Object.keys(result.error.flatten().fieldErrors).forEach((fieldName) => {
          form.trigger(fieldName as keyof EducationFormData);
        });

        return false;
      }
    },
    [educationSchema, append, update, form, editingIndex],
  );

  const handleCancelEducation = useCallback(() => {
    // Only reset form and go back to list view
    // Don't add empty fields to the array
    form.reset();
    setEditingIndex(null);
    setCurrentStep(0);
  }, [form]);

  const handleEditEducation = useCallback(
    (index: number) => {
      const education = fields[index];

      setEditingIndex(index);
      form.reset(education);
      setCurrentStep(1);
    },
    [fields, form],
  );

  const formatDateRange = useCallback(
    (startDate?: Date | string, endDate?: Date | string) => {
      if (!startDate) return "";

      const start = format(new Date(startDate), "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      if (!endDate) {
        return `${start} - ${t("forms:education.present")}`;
      }

      const end = format(new Date(endDate), "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      return `${start} - ${end}`;
    },
    [i18n.language, t],
  );

  // Memoized animation variants
  const containerVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          when: "beforeChildren",
          staggerChildren: 0.1,
          delayChildren: 0.2,
        },
      },
    }),
    [],
  );

  const stepVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
        },
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.2,
        },
      },
    }),
    [],
  );

  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, scale: 0.95 },
      visible: (i: number) => ({
        opacity: 1,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
          delay: i * 0.1,
        },
      }),
      exit: {
        opacity: 0,
        scale: 0.95,
        transition: {
          duration: 0.2,
          ease: "easeInOut",
        },
      },
    }),
    [],
  );

  // Step 0: Education List
  const EducationListStep = useMemo(() => {
    const EducationList = () => (
      <motion.div
        animate="visible"
        className="flex h-full w-full flex-col"
        exit="exit"
        initial="hidden"
        variants={stepVariants}
      >
        {/* Compact Header */}
        <div className="mb-6 text-center">
          <div className="mb-3 flex items-center justify-center gap-2">
            <div className="bg-primary flex items-center justify-center rounded-full p-2">
              <Icon
                className="text-accent-foreground"
                height="1rem"
                icon="mdi:school-outline"
                width="1rem"
              />
            </div>
            <h2 className="text-base font-semibold sm:text-lg">
              {t("profile:educationSection.title")}
            </h2>
            <Badge className="bg-primary text-accent-foreground text-xs">
              {t("common:optional")}
            </Badge>
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm">
            {t("profile:educationAndCertificates.educationDescription")}
          </p>
        </div>

        {/* Fixed Height List Area */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-col">
            <ScrollArea className="h-[60vh] lg:h-[600px]">
              <div className="space-y-3">
                {fields.length > 0 && (
                  <div className="flex justify-center pt-4">
                    <Button
                      className="bg-accent-foreground text-primary hover:bg-accent-foreground/90"
                      onClick={() => {
                        setEditingIndex(null);
                        form.reset();
                        setCurrentStep(1);
                      }}
                    >
                      <Icon className="mr-2" height="1rem" icon="mdi:plus" width="1rem" />
                      {t("forms:education.addEducation")}
                    </Button>
                  </div>
                )}
                <AnimatePresence mode="popLayout">
                  {fields.map((field, index) => (
                    <motion.div
                      key={field.id}
                      layout
                      animate="visible"
                      custom={index}
                      exit="exit"
                      initial="hidden"
                      variants={cardVariants}
                    >
                      <div className="group bg-side-accent hover:bg-side-accent/50 relative overflow-hidden rounded-lg p-3 shadow-sm transition-all">
                        <div className="flex items-start justify-between">
                          <button
                            className="flex min-w-0 flex-1 cursor-pointer items-start space-x-4 border-none bg-transparent text-left outline-none"
                            onClick={() => handleEditEducation(index)}
                          >
                            <div className="bg-accent-foreground/10 mt-1 flex-shrink-0 rounded-xl p-2">
                              <Icon
                                className="text-accent-foreground"
                                height="1.25rem"
                                icon="mdi:school-outline"
                                width="1.25rem"
                              />
                            </div>
                            <div className="min-w-0 flex-1 space-y-2">
                              <h4 className="text-accent-foreground text-base font-semibold">
                                {field.institution_name}
                              </h4>
                              <div className="flex flex-wrap gap-3">
                                {field.degree && (
                                  <div className="bg-accent-foreground/5 rounded-xl px-3 py-1">
                                    <p className="text-accent-foreground/80 text-sm font-medium">
                                      {field.degree}
                                    </p>
                                  </div>
                                )}
                                {field.field_of_study && (
                                  <p className="text-muted-foreground flex items-center text-sm">
                                    <Icon className="mr-2 h-4 w-4" icon="mdi:book-open-outline" />
                                    {field.field_of_study}
                                  </p>
                                )}
                              </div>
                              {(field.start_date || field.end_date) && (
                                <p className="text-muted-foreground flex items-center text-sm">
                                  <Icon className="mr-2 h-4 w-4" icon="mdi:calendar-outline" />
                                  {formatDateRange(field.start_date, field.end_date)}
                                </p>
                              )}
                              {field.description && (
                                <div className="bg-accent-foreground/5 mt-2 rounded-xl p-3">
                                  <p className="text-muted-foreground mb-1 text-xs font-medium">
                                    {t("forms:education.description")}
                                  </p>
                                  <p className="text-secondary-foreground line-clamp-2 text-sm">
                                    {field.description}
                                  </p>
                                </div>
                              )}
                            </div>
                          </button>
                          <Button
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(index);
                            }}
                          >
                            <Icon height="0.875rem" icon="mdi:trash-outline" width="0.875rem" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {fields.length === 0 && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-center p-6"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex max-w-lg flex-col items-center text-center">
                      <motion.div
                        animate={{ scale: 1, rotate: 0 }}
                        className="bg-accent-foreground mb-6 flex items-center justify-center rounded-full p-6"
                        initial={{ scale: 0.8, rotate: -10 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.2,
                          type: "spring",
                          stiffness: 200,
                          damping: 15,
                        }}
                      >
                        <Icon
                          className="text-primary"
                          height="3rem"
                          icon="mdi:school-outline"
                          width="3rem"
                        />
                      </motion.div>
                      <motion.h3
                        animate={{ opacity: 1 }}
                        className="text-accent-foreground mb-2 text-lg font-medium"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                      >
                        {t("forms:education.noEducation")}
                      </motion.h3>
                      <motion.p
                        animate={{ opacity: 1 }}
                        className="text-muted-foreground mb-6 max-w-lg text-sm leading-relaxed"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                      >
                        {t("forms:education.noEducationSubtitle")}
                      </motion.p>
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                      >
                        <Button
                          className="bg-accent-foreground text-primary hover:bg-accent-foreground/90 gap-2"
                          onClick={() => {
                            setEditingIndex(null);
                            form.reset();
                            setCurrentStep(1);
                          }}
                        >
                          <Icon height="1rem" icon="mdi:plus" width="1rem" />
                          {t("forms:education.addEducation")}
                        </Button>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </motion.div>
    );

    EducationList.displayName = "EducationList";

    return EducationList;
  }, [stepVariants, t, fields, formatDateRange, remove, cardVariants, handleEditEducation, form]);

  // Step 1: Add New Education
  const AddEducationStep = useMemo(() => {
    const AddEducation = () => {
      return (
        <motion.div
          animate="visible"
          className="flex h-full w-full flex-col"
          exit="exit"
          initial="hidden"
          variants={stepVariants}
        >
          {/* Compact Header */}
          <div className="mb-6 text-center">
            <div className="mb-3 flex items-center justify-center gap-2">
              <div className="bg-primary flex items-center justify-center rounded-full p-2">
                <Icon
                  className="text-accent-foreground"
                  height="1rem"
                  icon="mdi:book-education-outline"
                  width="1rem"
                />
              </div>
              <h2 className="text-lg font-semibold">
                {editingIndex !== null
                  ? t("forms:education.editEducation")
                  : t("forms:education.addEducation")}
              </h2>
              <Badge className="bg-primary text-accent-foreground text-xs">
                {t("common:optional")}
              </Badge>
            </div>
          </div>

          {/* Compact Form */}
          <div className="flex-1">
            <ScrollArea className="h-[60vh] lg:h-[600px]">
              <div className="space-y-5 p-1">
                {/* Row 1: Institution & Timeline */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent rounded-xl p-5 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {/* Institution Section */}
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:school"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:education.institutionDetails")}
                          </h3>
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="institution_name"
                        render={({ field }) => (
                          <FormItem>
                            <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                              <Icon height="0.875rem" icon="mdi:bank" width="0.875rem" />
                              {t("forms:education.institutionName")}
                            </div>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  className="h-10 pr-10 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:education.institutionPlaceholder")}
                                  {...field}
                                />
                                <div className="absolute inset-y-0 right-3 flex items-center">
                                  <Icon
                                    className={cn(
                                      "transition-colors",
                                      field.value.length >= 3
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground",
                                    )}
                                    height="0.875rem"
                                    icon={
                                      field.value.length >= 3
                                        ? "mdi:check-circle"
                                        : "mdi:close-circle-outline"
                                    }
                                    width="0.875rem"
                                  />
                                </div>
                              </div>
                            </FormControl>
                            <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                              <ErrorMessage
                                errors={form.formState.errors}
                                name="institution_name"
                              />
                            </p>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Timeline Section */}
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:timeline-clock"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:education.timeline")}
                          </h3>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          control={form.control}
                          name="start_date"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-1 text-sm font-medium">
                                <Icon
                                  height="0.875rem"
                                  icon="mdi:calendar-start"
                                  width="0.875rem"
                                />
                                {t("forms:education.startDate")}
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      className={cn(
                                        "border-input bg-background h-10 w-full justify-start border px-3 text-left text-xs font-normal transition-all focus-visible:ring-1 focus-visible:ring-offset-1",
                                        !field.value && "text-muted-foreground",
                                      )}
                                      variant="outline"
                                    >
                                      <Icon
                                        className="mr-2"
                                        height="0.875rem"
                                        icon="mdi:calendar-outline"
                                        width="0.875rem"
                                      />
                                      {field.value ? (
                                        <span className="text-accent-foreground">
                                          {format(new Date(field.value), "MM/yyyy", {
                                            locale: getDateFnsLocale(i18n.language),
                                          })}
                                        </span>
                                      ) : (
                                        <span>{t("forms:education.selectDate")}</span>
                                      )}
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                  <CalendarWithYearSelect
                                    initialFocus
                                    disabled={(date) => date > new Date()}
                                    fromYearSelect={new Date().getFullYear() - 49}
                                    language={i18n.language}
                                    mode="single"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    weekStartsOn={1}
                                    onSelect={(date) =>
                                      field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage errors={form.formState.errors} name="start_date" />
                              </p>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="end_date"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-1 text-sm font-medium">
                                <Icon height="0.875rem" icon="mdi:calendar-end" width="0.875rem" />
                                {t("forms:education.endDate")}
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      className={cn(
                                        "border-input bg-background h-10 w-full justify-start border px-3 text-left text-xs font-normal transition-all focus-visible:ring-1 focus-visible:ring-offset-1",
                                        !field.value && "text-muted-foreground",
                                        !form.watch("start_date") && "opacity-50",
                                      )}
                                      disabled={!form.watch("start_date")}
                                      variant="outline"
                                    >
                                      <Icon
                                        className="mr-2"
                                        height="0.875rem"
                                        icon="mdi:calendar-outline"
                                        width="0.875rem"
                                      />
                                      {field.value ? (
                                        <span className="text-accent-foreground">
                                          {format(new Date(field.value), "MM/yyyy", {
                                            locale: getDateFnsLocale(i18n.language),
                                          })}
                                        </span>
                                      ) : (
                                        <span>{t("forms:education.selectDate")}</span>
                                      )}
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                  <CalendarWithYearSelect
                                    initialFocus
                                    disabled={(date) => {
                                      const startDate = form.watch("start_date");

                                      return startDate ? date < toDate(startDate) : false;
                                    }}
                                    fromYearSelect={new Date().getFullYear() - 49}
                                    language={i18n.language}
                                    mode="single"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    weekStartsOn={1}
                                    onSelect={(date) =>
                                      field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage errors={form.formState.errors} name="end_date" />
                              </p>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Row 2: Academic Details & Description */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent rounded-xl p-5 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="grid grid-cols-2 flex-wrap gap-5">
                    {/* Academic Details Section */}
                    <div className={"max-lg:col-span-2"}>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:book-open-variant"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:education.academicDetails")}
                          </h3>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="degree"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                                <Icon height="0.875rem" icon="mdi:certificate" width="0.875rem" />
                                {t("forms:education.degree")}
                              </div>
                              <FormControl>
                                <Input
                                  className="h-10 pr-3 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:education.degreePlaceholder")}
                                  {...field}
                                />
                              </FormControl>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage errors={form.formState.errors} name="degree" />
                              </p>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="field_of_study"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                                <Icon
                                  height="0.875rem"
                                  icon="mdi:book-open-outline"
                                  width="0.875rem"
                                />
                                {t("forms:education.fieldOfStudy")}
                              </div>
                              <FormControl>
                                <Input
                                  className="h-10 pr-3 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:education.fieldPlaceholder")}
                                  {...field}
                                />
                              </FormControl>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage
                                  errors={form.formState.errors}
                                  name="field_of_study"
                                />
                              </p>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Description Section */}
                    <div className={"max-lg:col-span-2"}>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:text-box-outline"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:education.additionalInfo")}
                          </h3>
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                              <Icon height="0.875rem" icon="mdi:text" width="0.875rem" />
                              {t("forms:education.description")}
                            </div>
                            <FormControl>
                              <div className="relative">
                                <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-[147px] w-full rounded-xl">
                                  <Textarea
                                    className="[field-sizing:content] min-h-[130px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                    placeholder={t("forms:education.descriptionPlaceholder")}
                                    {...field}
                                  />
                                </ScrollArea>
                                <div className="text-muted-foreground absolute right-3 bottom-2 text-xs">
                                  {field.value?.length || 0}/1000
                                </div>
                              </div>
                            </FormControl>
                            <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                              <ErrorMessage errors={form.formState.errors} name="description" />
                            </p>
                          </FormItem>
                        )}
                      />
                    </div>
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      className="col-span-2 flex justify-between pt-5"
                      initial={{ opacity: 0, y: 20 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Button
                        className="h-11 px-6"
                        size="lg"
                        variant="outline"
                        onClick={handleCancelEducation}
                      >
                        <Icon
                          className="mr-1"
                          height="1rem"
                          icon="mdi:cancel-circle-outline"
                          width="1rem"
                        />
                        {t("common:cancel")}
                      </Button>
                      <Button
                        className="bg-accent-foreground text-primary hover:bg-accent-foreground/90 ms-auto h-11 px-8"
                        size="lg"
                        onClick={() => handleAddEducation(form.getValues())}
                      >
                        <Icon className="mr-1" height="1rem" icon="mdi:check-circle" width="1rem" />
                        {editingIndex !== null ? t("common:update") : t("common:save")}
                      </Button>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </ScrollArea>
          </div>
        </motion.div>
      );
    };

    AddEducation.displayName = "AddEducation";

    return AddEducation;
  }, [stepVariants, t, form, handleAddEducation, i18n.language, fields, editingIndex, handleCancelEducation]);

  return (
    <motion.div
      animate="visible"
      className="flex h-full w-full flex-col items-center justify-center p-4 text-start md:p-6"
      initial="hidden"
      variants={containerVariants}
    >
      <div className={"my-auto w-full"}>
        <AnimatePresence mode="wait">
          {currentStep === 0 ? (
            <EducationListStep key="education-list" />
          ) : (
            <AddEducationStep key="education-add" />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
